/**
 * Resolvers for the Nova GAME_API and Core API base URLs.
 *
 * Single shared implementation of the (previously duplicated) logic each
 * game's `main.ts` used to define inline. Centralizes the existing chain so
 * games, the arcade SDK bootstrap, and `EnvUtils` all consume it from
 * `@nova/network`.
 *
 * Resolution order (first match wins) — shared by both helpers:
 *   1. Per-helper `localStorage` override (only when `IS_LOCAL=true`):
 *        - `getGameApiUrl()` -> `localStorage.overrideGameApiUrl`
 *        - `getCoreApiUrl()` -> `localStorage.overrideCoreApiUrl`
 *      Used by the webApp -> game local-dev redirect flow.
 *   2. `localStorage.CoreApiUrl` (set by the webApp local-dev redirect).
 *      `getGameApiUrl()` appends `/api/nova`; `getCoreApiUrl()` returns it as-is.
 *   3. `CORE_API_URL` (the literal `KHWEBAPP_COREURL` placeholder, swapped
 *      per-environment at deploy time by `.github/actions/deploy-webapp`'s
 *      `update-khwebapp-values.ps1`). Same mechanism the webApp itself uses
 *      via `WebApp/src/environments/environment.cloudflare.ts`.
 *   4. Build-time fallback baked in by dotenv-webpack (used in local dev
 *      where the deploy script never runs):
 *        - `getGameApiUrl()` -> `process.env.GAME_API`
 *        - `getCoreApiUrl()` -> `process.env.GAME_API` with the
 *          `/api/nova` suffix stripped (games' bundles only define
 *          `GAME_API`, not a separate core URL).
 */

const CORE_API_URL = 'KHWEBAPP_COREURL';

function trimTrailingSlash(url: string): string {
  return url.replace(/\/$/, '');
}

/**
 * Shared resolution for steps 2 and 3. Returns the bare core base URL
 * (no `/api/nova` suffix, no trailing slash) or `null` when neither
 * source is available.
 */
function resolveCoreBaseUrl(): string | null {
  if (typeof localStorage !== 'undefined') {
    const localCoreApiUrl = localStorage.getItem('CoreApiUrl');
    if (localCoreApiUrl) {
      return trimTrailingSlash(localCoreApiUrl);
    }
  }

  // `RegExp.prototype.test` defeats terser's constant-folding so this branch
  // survives minification; with `String.prototype.startsWith` terser would
  // evaluate the placeholder string at build time and dead-code the branch,
  // breaking the deploy-time substitution.
  if (/^https?:/.test(CORE_API_URL)) {
    return trimTrailingSlash(CORE_API_URL);
  }

  return null;
}

/**
 * Returns the Nova GAME_API base URL (i.e. the core URL with `/api/nova`).
 */
export function getGameApiUrl(): string {
  // NOTE: `process.env.X` must be referenced as the literal pattern so
  // webpack's DefinePlugin (via dotenv-webpack in the games' bundles) can
  // statically replace it at build time. Aliasing `const env = process.env`
  // would break that replacement and yield `undefined` at runtime.
  if (typeof localStorage !== 'undefined' && process.env.IS_LOCAL === 'true') {
    const override = localStorage.getItem('overrideGameApiUrl');
    if (override) { return override; }
  }

  const base = resolveCoreBaseUrl();
  if (base) { return `${base}/api/nova`; }

  return process.env.GAME_API ?? '';
}

/**
 * Returns the Core API base URL (no `/api/nova` suffix).
 *
 * Used by the arcade SDK so that analytics and other Core endpoints land in
 * the matching deploy environment instead of being hardcoded to a single
 * test env.
 */
export function getCoreApiUrl(): string {
  if (typeof localStorage !== 'undefined' && process.env.IS_LOCAL === 'true') {
    const override = localStorage.getItem('overrideCoreApiUrl');
    if (override) { return trimTrailingSlash(override); }
  }

  const base = resolveCoreBaseUrl();
  if (base) { return base; }

  // Fallback: derive the core URL from the build-time GAME_API by stripping
  // the `/api/nova` suffix. Games' webpack only defines `GAME_API`, so this
  // keeps standalone local-dev runs (no webApp redirect, no deploy
  // substitution) working without requiring a new env var per game.
  const gameApi = process.env.GAME_API;
  if (gameApi) { return trimTrailingSlash(gameApi).replace(/\/api\/nova$/, ''); }

  return '';
}
